import { DatePicker } from '@/components/commons/date-picker-custom';
import { SignupElementDataType } from '@/components/sections/builder/elements/body/signup/type';
import { ElementProps } from '@/components/sections/builder/types';
import FieldErrorMessage from '@/components/sections/field-error-message';
import { Button } from '@/components/ui/button';
import { FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { signup } from '@/lib/actions/customer/action';
import { SendEventMetapixel } from '@/lib/analytic-send-event';
import { cn, isEmpty, removeHTMLTags } from '@/lib/utils';
import { useBuilderStore } from '@/stores/builder';
import { format } from 'date-fns';
import { Form, Formik } from 'formik';
import { Eye, EyeClosed } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import React from 'react';

import * as Yup from 'yup';
import { shallow } from 'zustand/shallow';

const SignupSchema = Yup.object().shape({
  first_name: Yup.string()
    .matches(
      /^[A-Za-z\s]+$/,
      'First Name and Last Name cannot contain numbers or special characters. Only letters and spaces are allowed.'
    )
    .min(3, 'First name must be at least 3 characters')
    .max(50, 'First name cannot be longer than 50 characters')
    .required('First name is required'),
  last_name: Yup.string()
    .matches(
      /^[A-Za-z\s]+$/,
      'First Name and Last Name cannot contain numbers or special characters. Only letters and spaces are allowed.'
    )
    .min(3, 'Last name must be at least 3 characters')
    .max(50, 'Last name cannot be longer than 50 characters')
    .required('Last name is required'),
  email: Yup.string().email('Invalid Format Email').required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .max(64, 'Password cannot be longer than 64 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(/[^A-Za-z0-9]/, 'Password must contain at least one special character')
    .matches(/^\S*$/, 'Password cannot contain spaces')
    .required('Password is required'),
  password_confirmation: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Password confirmation is required'),
});

const initialValues = {
  first_name: '',
  last_name: '',
  dob: new Date(),
  email: '',
  password: '',
  password_confirmation: '',
};

export default function ElementContentSignup({ element, host, theme, preview }: ElementProps) {
  const router = useRouter();
  const query = useSearchParams();
  const isPreview = preview || false;
  const token = query.get('token') || '';
  const themeId = query.get('theme_id') || '';
  const checkout_url = query.get('checkout_url');

  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = React.useState<boolean>(false);

  const content = element.data as SignupElementDataType;

  const builderTheme = useBuilderStore((state) => state.theme, shallow);
  const themeContent = preview ? builderTheme.input : theme?.input;

  const handleOnSubmit = async (values: typeof initialValues) => {
    if (isPreview || !!token) return;

    try {
      setIsSubmitted(true);

      const payload = {
        ...values,
        dob: format(values.dob, 'yyyy-MM-dd'),
      };
      const result = await signup(host as string, payload);

      if (result.success) {
        toast({
          title: 'Success',
          description:
            result?.message ||
            'Sign up successfully, please check your email to verify your account.',
          variant: 'success',
        });

        SendEventMetapixel('CompleteRegistration', {
          currency: 'IDR',
          content_type: 'customer',
          value: 0,
          quantity: 1,
          contents: [
            {
              customer_email: payload.email,
              customer_name: `${payload.first_name} ${payload.last_name}`,
              customer_dob: payload.dob,
            },
          ],
        });

        if (checkout_url) {
          router.replace(`/account/signin?checkout_url=${encodeURIComponent(checkout_url)}`);
        } else {
          router.replace('/account/signin');
        }
      } else {
        toast({
          title: 'Error',
          description: result?.message || 'Failed to sign up',
          variant: 'destructive',
        });
      }
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Error',
          description: error.message || 'Something went wrong.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsSubmitted(false);
    }
  };

  const handleShowPassword = (field: string) => {
    if (field === 'password') {
      setShowPassword((prev) => !prev);
    } else {
      setShowPasswordConfirmation((prev) => !prev);
    }
  };

  const headingLabel =
    removeHTMLTags(content.headingLabel || '').trim() !== '' ? content.headingLabel : 'Sign up';

  return (
    <>
      <div
        className='bg_scheme_signout flex h-full min-h-[20vh] w-full items-center justify-center px-4 md:pb-[--padding-bottom] md:pt-[--padding-top]'
        style={
          {
            '--btn-label': content?.colorScheme?.btnSolidlabel,
            '--border-color': content?.colorScheme?.btnSolidBackgroud,
            '--rounded': `${themeContent?.borderCornerRadius || 8}px`,
            '--padding-top': `${content.topPadding || 0}px`,
            '--padding-bottom': `${content.bottomPadding || 0}px`,
          } as React.CSSProperties
        }
      >
        <div className='flex w-full max-w-[720px] flex-col items-center'>
          <div className='flex w-full items-start'>
            <div
              className='mb-6 text-lg md:text-xl lg:text-2xl'
              dangerouslySetInnerHTML={{ __html: headingLabel as string }}
            />
          </div>
          <div className='w-full'>
            <Formik
              key={JSON.stringify(initialValues)}
              initialValues={initialValues}
              validationSchema={SignupSchema}
              onSubmit={handleOnSubmit}
            >
              {({ values, errors, handleSubmit, handleChange, handleBlur, touched }) => (
                <Form
                  onSubmit={handleSubmit}
                  className={cn(
                    'flex flex-col gap-6',
                    content?.layout === 'grid' ? 'md:grid md:grid-cols-2' : 'md:flex md:flex-col'
                  )}
                >
                  <FormItem>
                    <div className='flex w-full flex-col'>
                      <Label htmlFor='first_name' className='mb-2 font-normal'>
                        First name
                      </Label>
                      <Input
                        id='first_name'
                        name='first_name'
                        type='text'
                        className={cn(
                          'rounded-[var(--rounded)] font-normal placeholder:text-sm placeholder:font-light focus:!outline-0 focus:!ring-0 active:outline-0 active:ring-0',
                          !isEmpty(content.colorScheme) &&
                            'focus:!border-[var(--border-color)] active:!border-[var(--border-color)]',
                          errors.first_name && touched.first_name && 'border-red-500'
                        )}
                        placeholder='Enter your first name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.first_name}
                        required
                        data-test='first_name'
                      />
                      {touched.first_name && errors.first_name && (
                        <FieldErrorMessage error={errors.first_name} />
                      )}
                    </div>
                  </FormItem>
                  <FormItem>
                    <div className='flex w-full flex-col'>
                      <Label htmlFor='last_name' className='mb-2 font-normal'>
                        Last name
                      </Label>
                      <Input
                        id='last_name'
                        name='last_name'
                        type='text'
                        className={cn(
                          'rounded-[var(--rounded)] font-normal placeholder:text-sm placeholder:font-light focus:!outline-0 focus:!ring-0 active:outline-0 active:ring-0',
                          !isEmpty(content.colorScheme) &&
                            'focus:!border-[var(--border-color)] active:!border-[var(--border-color)]',
                          errors.last_name && touched.last_name && 'border-red-500'
                        )}
                        placeholder='Enter your last name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.last_name}
                        data-test='last_name'
                        required
                      />
                      {touched.last_name && errors.last_name && (
                        <FieldErrorMessage error={errors.last_name} />
                      )}
                    </div>
                  </FormItem>
                  <FormItem>
                    <div className='flex w-full flex-col'>
                      <Label htmlFor='email' className='mb-2 font-normal'>
                        Email
                      </Label>
                      <Input
                        id='email'
                        name='email'
                        type='email'
                        className={cn(
                          'rounded-[var(--rounded)] font-normal placeholder:text-sm placeholder:font-light focus:!outline-0 focus:!ring-0 active:outline-0 active:ring-0',
                          !isEmpty(content.colorScheme) &&
                            'focus:!border-[var(--border-color)] active:!border-[var(--border-color)]',
                          errors.email && touched.email && 'border-red-500'
                        )}
                        classNameWrapper='w-full'
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder='Enter your email'
                        required
                      />
                      {errors.email && touched.email && <FieldErrorMessage error={errors.email} />}
                    </div>
                  </FormItem>
                  <FormItem className='flex flex-col'>
                    <Label htmlFor='dob' className='font-normal'>
                      Date of birth
                    </Label>
                    <DatePicker
                      className='rounded-[var(--rounded)]'
                      value={values.dob}
                      data-test='dob'
                      onChange={(value) => {
                        handleChange({
                          target: {
                            name: 'dob',
                            value,
                          },
                        });
                      }}
                      colorSelectedDay={{
                        color: content?.colorScheme?.btnSolidlabel || '#fff',
                        backgroundColor: content?.colorScheme?.btnSolidBackgroud || '#000',
                      }}
                      placeholder='Enter your date of birth'
                      isCustom
                    />
                  </FormItem>
                  <FormItem>
                    <div className='flex w-full flex-col'>
                      <Label htmlFor='password' className='mb-2 font-normal'>
                        Password
                      </Label>
                      <div className='relative'>
                        <Input
                          id='password'
                          name='password'
                          type={showPassword ? 'text' : 'password'}
                          className={cn(
                            'rounded-[var(--rounded)] font-normal placeholder:text-sm placeholder:font-light focus:!outline-0 focus:!ring-0 active:outline-0 active:ring-0',
                            !isEmpty(content.colorScheme) &&
                              'focus:!border-[var(--border-color)] active:!border-[var(--border-color)]',
                            errors.password &&
                              touched.password &&
                              typeof errors.password === 'string' &&
                              'border-red-500'
                          )}
                          classNameWrapper='w-full'
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder='Create your password'
                          required
                          autoComplete='off'
                          data-test='password'
                        />
                        <button
                          type='button'
                          onClick={() => handleShowPassword('password')}
                          className='absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-500'
                        >
                          {showPassword ? (
                            <EyeClosed className='h-4 w-4' />
                          ) : (
                            <Eye className='h-4 w-4' />
                          )}
                        </button>
                      </div>
                      {errors.password && touched.password && (
                        <FieldErrorMessage
                          error={errors.password && touched.password ? errors.password : ''}
                        />
                      )}
                    </div>
                  </FormItem>
                  <FormItem>
                    <div className='flex w-full flex-col'>
                      <Label htmlFor='password_confirmation' className='mb-2 font-normal'>
                        Confirm password
                      </Label>
                      <div className='relative'>
                        <Input
                          id='password_confirmation'
                          name='password_confirmation'
                          type={showPasswordConfirmation ? 'text' : 'password'}
                          className={cn(
                            'rounded-[var(--rounded)] font-normal placeholder:text-sm placeholder:font-light focus:!outline-0 focus:!ring-0 active:outline-0 active:ring-0',
                            !isEmpty(content.colorScheme) &&
                              'focus:!border-[var(--border-color)] active:!border-[var(--border-color)]',
                            errors.password_confirmation &&
                              touched.password_confirmation &&
                              typeof errors.password_confirmation === 'string' &&
                              'border-red-500'
                          )}
                          value={values.password_confirmation}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder='Re-enter your password'
                          classNameWrapper='w-full'
                          required
                          autoComplete='off'
                          data-test='password_confirmation'
                        />
                        <button
                          type='button'
                          onClick={() => handleShowPassword('password_confirmation')}
                          className='absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-500'
                        >
                          {showPasswordConfirmation ? (
                            <EyeClosed className='h-4 w-4' />
                          ) : (
                            <Eye className='h-4 w-4' />
                          )}
                        </button>
                      </div>
                      {errors.password_confirmation && touched.password_confirmation && (
                        <FieldErrorMessage
                          error={
                            errors.password_confirmation && touched.password_confirmation
                              ? errors.password_confirmation
                              : ''
                          }
                        />
                      )}
                    </div>
                  </FormItem>
                  <div
                    className={cn(
                      'flex flex-col gap-6',
                      content?.layout === 'grid' && 'md:col-span-2'
                    )}
                  >
                    <p
                      className='text-sm font-normal'
                      style={{ color: content.colorScheme?.textSecondary }}
                    >
                      Your personal data will be used to support your experience throughout this
                      website, to manage access to your account, and for other purposes described in{' '}
                      <b>our privacy policy</b>.
                    </p>
                    <Button
                      className='theme_button w-full transition-all duration-300 hover:opacity-80 disabled:hover:cursor-not-allowed'
                      type='submit'
                      style={
                        !isEmpty(content.colorScheme)
                          ? {
                              backgroundColor: content.colorScheme?.btnSolidBackgroud,
                              color: content?.colorScheme?.btnSolidlabel,
                            }
                          : {}
                      }
                      disabled={(isPreview as boolean) || isSubmitted || !!token}
                    >
                      <span className='text-sm'>
                        {isSubmitted ? 'Please wait...' : content?.buttonLabel || 'Sign up'}
                      </span>
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
            <div className='mt-6 items-center text-center text-sm'>
              Already have an account?{' '}
              <Link
                href={`/account/signin${token ? `?theme_id=${themeId}&token=${token}` : ''}`}
                className='inline-flex items-center text-sm'
                data-test='link-signin'
                style={
                  !isEmpty(content.colorScheme)
                    ? {
                        color: content?.colorScheme?.btnSolidBackgroud,
                      }
                    : {}
                }
              >
                Sign in here
              </Link>
            </div>
          </div>
        </div>
      </div>
      <style jsx>
        {`
          .bg_scheme_signout {
            background-color: ${content.colorScheme?.background || 'white'};
            color: ${content.colorScheme?.text || '#52535B'};
          }
          .signup_padding {
            padding-top: ${content.topPadding || 0}px;
            padding-bottom: ${content.bottomPadding || 0}px;
          }
          @media (max-width: 640px) {
            .signup_padding {
              padding-left: 20px;
              padding-right: 20px;
            }
          }
        `}
      </style>
    </>
  );
}
